import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, QrCode } from "lucide-react";
import { toast } from "sonner";

// Small inline QR that opens a modal with a big scannable QR + the seat link.
// Designed for the dark green lobby header card (light QR on cream bg).
// Props:
//   url        - seat link to encode (required)
//   username   - who this seat belongs to (for modal title)
//   inlineSize - px size of the inline QR (default 48)
//   testId     - data-testid prefix; the inline trigger is `${testId}-trigger`
//                and the modal's copy button is `${testId}-copy`
//   variant    - "dark" (default, white bg pill for the green header card)
//                | "light" (transparent — for white participant rows)
export default function SeatLinkQR({
  url,
  username,
  inlineSize = 48,
  testId = "seat-qr",
  variant = "dark",
}) {
  const [open, setOpen] = useState(false);

  if (!url) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Seat link copied");
    } catch {
      toast.error("Couldn't copy — long-press the link instead");
    }
  };

  const trigger =
    variant === "dark" ? (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={
          username
            ? `Show QR for ${username}'s seat link`
            : "Show QR for your seat link"
        }
        aria-label={
          username
            ? `Show QR for ${username}'s seat link`
            : "Show QR for your seat link"
        }
        className="bg-white rounded-md p-1 ring-1 ring-pbo-gold/40 hover:ring-pbo-gold transition-all shrink-0"
        data-testid={`${testId}-trigger`}
      >
        <QRCodeSVG
          value={url}
          size={inlineSize}
          level="M"
          fgColor="#0F4D2A"
          bgColor="#FFFFFF"
        />
      </button>
    ) : (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={
          username
            ? `Show QR for ${username}'s seat link`
            : "Show QR for your seat link"
        }
        aria-label={
          username
            ? `Show QR for ${username}'s seat link`
            : "Show QR for your seat link"
        }
        className="h-7 px-1.5 text-pbo-muted hover:text-pbo-green transition-colors rounded shrink-0"
        data-testid={`${testId}-trigger`}
      >
        <QrCode className="w-3.5 h-3.5" />
      </button>
    );

  return (
    <>
      {trigger}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-pbo-cream sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-pbo-green text-xl">
              {username ? `${username}'s seat link` : "Your seat link"}
            </DialogTitle>
            <DialogDescription className="text-pbo-ink/70">
              Scan this code with a phone camera to drop straight into the draft
              {username ? ` as ${username}` : ""}. The link is single-seat — anyone
              who opens it joins as that person.
            </DialogDescription>
          </DialogHeader>

          <div
            className="flex justify-center py-4"
            data-testid={`${testId}-large`}
          >
            <div className="bg-white p-4 rounded-xl ring-1 ring-pbo-gold/40 shadow-sm">
              <QRCodeSVG
                value={url}
                size={240}
                level="M"
                fgColor="#0F4D2A"
                bgColor="#FFFFFF"
                includeMargin={false}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-pbo-muted">
              Or copy the link
            </div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={url}
                onClick={(e) => e.target.select()}
                className="flex-1 text-xs bg-white border border-pbo-border rounded px-2 py-2 font-mono text-pbo-ink truncate"
                data-testid={`${testId}-url`}
              />
              <Button
                variant="outline"
                onClick={copy}
                className="border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream h-9 px-3"
                data-testid={`${testId}-copy`}
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" />
                Copy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
